import React, { useEffect, useRef, useState } from "react";
import { Button } from "antd";
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  CloseOutlined,
  MinusOutlined,
  PlusOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
} from "@ant-design/icons";
import { Rnd } from "react-rnd";

type RightSidePreviewWindowProps = {
  open: boolean;
  handleCancel: () => void;
  documentUrl: string | undefined | null;
  setOpen: (value: boolean) => void;
};

const RightSidePreviewWindow: React.FC<RightSidePreviewWindowProps> = ({
  open,
  handleCancel,
  documentUrl,
  setOpen,
}) => {
  const isPdf = documentUrl?.toLowerCase().endsWith(".pdf");
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotationAngle, setRotationAngle] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const scaleFactor = 0.67;
  const [size, setSize] = useState({
    width: window.innerWidth / 2,
    height: window.innerHeight,
  });
  // Add state to track position
  const [position, setPosition] = useState({
    x: window.innerWidth - window.innerWidth / 2,
    y: 0,
  });
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleZoomIn = () =>
    setZoomLevel((prevZoom) => Math.min(prevZoom + 0.2, 4));

  const handleZoomOut = () =>
    setZoomLevel((prevZoom) => Math.max(prevZoom - 0.2, 0.5));

  const handleRotateLeft = () => setRotationAngle((prev) => prev - 90);
  const handleRotateRight = () => setRotationAngle((prev) => prev + 90);

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  useEffect(() => {
    setZoomLevel(1);
    setRotationAngle(0);
  }, [documentUrl]);

  // Handle size changes
  const handleResize = (
    e: any,
    direction: any,
    ref: any,
    delta: any,
    position: any
  ) => {
    setSize({
      width: parseInt(ref.style.width),
      height: parseInt(ref.style.height),
    });
    setPosition(position);
  };

  // Handle drag
  const handleDrag = (e: any, data: any) => {
    setPosition({ x: data.x, y: data.y });
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <Rnd
        scale={scaleFactor}
        size={{
          width: isMinimized ? Math.min(size.width, 300) : size.width,
          height: isMinimized ? 40 : size.height,
        }}
        position={position}
        minWidth={200}
        minHeight={isMinimized ? 40 : 850}
        maxWidth={window.innerWidth}
        maxHeight={window.innerHeight}
        bounds="window"
        enableResizing={{
          top: !isMinimized,
          right: !isMinimized,
          bottom: !isMinimized,
          left: !isMinimized,
          topRight: !isMinimized,
          bottomRight: !isMinimized,
          bottomLeft: !isMinimized,
          topLeft: !isMinimized,
        }}
        dragHandleClassName="custom-drag-handle"
        onResize={handleResize}
        onDragStop={handleDrag}
        style={{
          zIndex: 1000,
          backgroundColor: "#fff",
          border: "1px solid #ccc",
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
          pointerEvents: "auto",
        }}
        resizeHandleStyles={{
          bottom: { height: "10px", cursor: "ns-resize" },
          bottomLeft: { height: "10px", width: "10px", cursor: "nesw-resize" },
          bottomRight: { height: "10px", width: "10px", cursor: "nwse-resize" },
          left: { width: "10px", cursor: "ew-resize" },
          right: { width: "10px", cursor: "ew-resize" },
          top: { height: "10px", cursor: "ns-resize" },
          topLeft: { height: "10px", width: "10px", cursor: "nwse-resize" },
          topRight: { height: "10px", width: "10px", cursor: "nesw-resize" },
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "#f0f0f0",
            padding: "0.5rem",
            cursor: "move",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            height: "40px",
            borderBottom: isMinimized ? "none" : "1px solid #ccc",
          }}
          className="custom-drag-handle"
        >
          <span>
            {isMinimized ? "Preview Window (Minimized)" : "Preview Window"}
          </span>
          <div style={{ display: "flex", gap: "4px" }}>
            <Button
              onClick={toggleMinimize}
              icon={isMinimized ? <PlusOutlined /> : <MinusOutlined />}
              size="small"
            />
            <Button
              onClick={handleCancel}
              icon={<CloseOutlined />}
              size="small"
            />
          </div>
        </div>
        {!isMinimized && (
          <div
            ref={containerRef}
            style={{
              height: "calc(100% - 40px)", // Subtract header height
              overflow: "auto", // Enable both horizontal and vertical scrolling
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              padding: "10px",
            }}
          >
            {isPdf ? (
              <object
                data={documentUrl || ""}
                type="application/pdf"
                style={{ width: "100%", height: "100%" }}
              >
                <p>
                  Unable to display file.{" "}
                  <a
                    href={documentUrl || ""}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Click here to view.
                  </a>
                </p>
              </object>
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  overflow: "auto", // Make the container scrollable
                }}
              >
                <img
                  ref={imgRef}
                  src={documentUrl || ""}
                  alt="Preview"
                  style={{
                    width: `${zoomLevel * 100}%`, // Zooming width
                    transform: `rotate(${rotationAngle}deg)`,
                    height: "auto", // Maintain aspect ratio
                    maxWidth: "none", // Remove max-width restriction
                  }}
                  draggable={false}
                />
              </div>
            )}

            {/* Zoom Controls */}
            {!isPdf && (
              <div
                style={{
                  position: "absolute",
                  bottom: "10px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  gap: "0.5rem",
                  zIndex: 1100,
                }}
              >
                <Button
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 0.5}
                  icon={<ZoomOutOutlined />}
                />
                <Button
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 4}
                  icon={<ZoomInOutlined />}
                />
                <Button onClick={handleRotateLeft} style={{ marginLeft: 8 }}>
                  <RotateLeftOutlined />
                </Button>
                <Button onClick={handleRotateRight} style={{ marginLeft: 8 }}>
                  <RotateRightOutlined />
                </Button>
              </div>
            )}
          </div>
        )}
      </Rnd>
    </div>
  );
};

export default RightSidePreviewWindow;
